import { eventApiClient } from '@src/api/tenant/event/eventApiClient';
import { EventErrorResponse, EventForm, EventSettingsFormProps } from '@src/api/types/event';
import { FORM_CONSTANTS } from '@src/api/types/supportService';
import { useScrollToError } from '@src/hooks/useScrollToError';
import { Switch } from '@src/ui/elements/form';
import FormField from '@src/ui/elements/form/FormField';
import ImageUploadField from '@src/ui/elements/form/ImageUploadField';
import Input from '@src/ui/elements/form/Input';
import InputCalendar from '@src/ui/elements/form/InputCalendar';
import PDFUploadField from '@src/ui/elements/form/PDFUploadField';
import Select from '@src/ui/elements/form/Select';
import TagField from '@src/ui/elements/form/TagField';
import Textarea from '@src/ui/elements/form/Textarea';
import { ChangeEvent, useCallback } from 'react';
import { useWatch } from 'react-hook-form';

import { useFileUpload } from '../../../../hooks/useFileUpload';
import { useTagManagement } from '../../../../hooks/useTagManagement';

const SettingForm: React.FC<EventSettingsFormProps> = ({
  categoryOptions,
  availableTags = [],
  setAvailableTags,
  errors: serverErrors = {} as EventErrorResponse,
  setErrors,
  methods,
}) => {
  const fieldRefs = useScrollToError(serverErrors);
  const [pdfUrl, originalPdfName] = useWatch({
    control: methods.control,
    name: ['pdf_url', 'original_pdf_name'],
  });
  const onChangeHandler = useCallback(
    (name: keyof EventForm, value: string | number | number[]) => {
      methods.setValue(name, value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [methods],
  );

  const { handleUpload, handleDelete, isUploading, previewBase64 } = useFileUpload({
    onSuccess: (url) => onChangeHandler('thumbnail_url', url),
    onError: (error) => setErrors?.({ ...serverErrors, thumbnail_url: [error] }),
    apiClientUploadFile: eventApiClient.uploadFile,
    apiClientDeleteFile: eventApiClient.deleteFile,
  });

  const {
    handleUpload: handlePdfUpload,
    handleDelete: handlePdfDelete,
    isUploading: isPdfUploading,
  } = useFileUpload({
    onSuccess: (url, originalFileName) => {
      onChangeHandler('pdf_url', url);
      onChangeHandler('original_pdf_name', originalFileName || '');
    },
    onError: (error) => setErrors?.({ ...serverErrors, pdf_url: [error] }),
    apiClientUploadFile: eventApiClient.uploadPdf,
    apiClientDeleteFile: eventApiClient.deleteFile,
  });

  const isTopDisplay = useWatch({ control: methods.control, name: 'is_top_display' });

  const {
    newTagName,
    setNewTagName,
    handleNewTag,
    handleAddTag,
    handleRemoveTag,
    tagError,
    selectedTags,
  } = useTagManagement({
    initialTags: methods.getValues('media_tags') as number[],
    availableTags,
    setAvailableTags,
    onTagsChange: (tags) => onChangeHandler('media_tags', tags),
    type: 'media',
  });

  return (
    <>
      <FormField
        control={methods.control}
        errors={serverErrors.title}
        name='title'
        label='イベント名'
        maxLength={FORM_CONSTANTS.MAX_TITLE_LENGTH}
        component={Input}
        placeholder='イベント'
        componentProps={{
          onChange: (e: ChangeEvent<HTMLInputElement>) => onChangeHandler('title', e.target.value),
        }}
        fieldRef={(el) => {
          fieldRefs.current.title = el;
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.event_start_at}
        name='event_start_at'
        label='イベント開始日'
        component={InputCalendar}
        componentProps={{
          initialValue: methods.getValues('event_start_at'),
          onChange: (value: string | null) =>
            onChangeHandler('event_start_at', value === null ? '' : value),
        }}
        fieldRef={(el) => {
          fieldRefs.current.event_start_at = el;
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.event_end_at}
        name='event_end_at'
        label='イベント終了日'
        component={InputCalendar}
        componentProps={{
          initialValue: methods.getValues('event_end_at'),
          onChange: (value: string | null) =>
            onChangeHandler('event_end_at', value === null ? '' : value),
        }}
        fieldRef={(el) => {
          fieldRefs.current.event_end_at = el;
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.recruitment_start_at}
        name='recruitment_start_at'
        label='募集開始日'
        component={InputCalendar}
        componentProps={{
          initialValue: methods.getValues('recruitment_start_at'),
          onChange: (value: string | null) =>
            onChangeHandler('recruitment_start_at', value === null ? '' : value),
        }}
        fieldRef={(el) => {
          fieldRefs.current.recruitment_start_at = el;
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.recruitment_end_at}
        name='recruitment_end_at'
        label='募集終了日'
        component={InputCalendar}
        componentProps={{
          initialValue: methods.getValues('recruitment_end_at'),
          onChange: (value: string | null) =>
            onChangeHandler('recruitment_end_at', value === null ? '' : value),
        }}
        fieldRef={(el) => {
          fieldRefs.current.recruitment_end_at = el;
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.published_at}
        name='published_at'
        label={
          <div className='flex items-end gap-[6px]'>
            <span>公開日</span>
            <span className='text-[10px]'>※09:00公開</span>
          </div>
        }
        component={InputCalendar}
        componentProps={{
          initialValue: methods.getValues('published_at'),
          onChange: (value: string | null) =>
            onChangeHandler('published_at', value === null ? '' : value),
        }}
        fieldRef={(el) => {
          fieldRefs.current.published_at = el;
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.media_category_id}
        name='media_category_id'
        label='カテゴリ'
        component={Select}
        componentProps={{
          items: categoryOptions?.map((category) => ({
            id: category.id.toString(),
            name: category.name,
          })),
          onSelect: (id: string) => onChangeHandler('media_category_id', id),
          initialValue: methods.getValues('media_category_id'),
        }}
        fieldRef={(el) => {
          fieldRefs.current.media_category_id = el;
        }}
      />

      <TagField
        newTagName={newTagName}
        setNewTagName={setNewTagName}
        tags={
          selectedTags.length > 0 ? selectedTags : (methods.getValues('media_tags') as number[])
        }
        onNewTag={handleNewTag}
        onAddTag={handleAddTag}
        onRemoveTag={handleRemoveTag}
        error={tagError}
        availableTags={availableTags}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.description}
        name='description'
        label='概要（description）'
        component={Textarea}
        placeholder='記事の概要を記入してください。検索結果やSNSでシェアしたときに Title とともに表示される概要文です。'
        maxLength={FORM_CONSTANTS.MAX_DESCRIPTION_LENGTH}
        componentProps={{
          onChange: (e: ChangeEvent<HTMLTextAreaElement>) =>
            onChangeHandler('description', e.target.value),
        }}
        fieldRef={(el) => {
          fieldRefs.current.description = el;
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.is_top_display}
        label='上位表示'
        labelPosition='left'
        name='is_top_display'
        component={Switch}
        componentProps={{
          onCheck: () => {
            methods.setValue('is_top_display', true, { shouldValidate: true, shouldDirty: true });
          },
          onUncheck: () => {
            methods.setValue('is_top_display', false, { shouldValidate: true, shouldDirty: true });
          },
          checked: isTopDisplay,
        }}
        fieldRef={(el) => {
          if (el) {
            fieldRefs.current.is_top_display = el;
          }
        }}
      />

      <ImageUploadField
        onUpload={handleUpload}
        onDelete={handleDelete}
        preview={previewBase64 || methods.getValues('thumbnail_url')}
        isUploading={isUploading}
        errors={serverErrors.thumbnail_url}
      />

      <PDFUploadField
        onFileUpload={handlePdfUpload}
        onDelete={handlePdfDelete}
        preview={pdfUrl}
        originalFileName={originalPdfName}
        isUploading={isPdfUploading}
        maxFileSize={25 * 1024 * 1024}
        errors={serverErrors.pdf_url}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.application_url}
        name='application_url'
        label='申込みフォームURL'
        component={Input}
        placeholder='申込みフォームURL'
        componentProps={{
          onChange: (e: ChangeEvent<HTMLInputElement>) =>
            onChangeHandler('application_url', e.target.value),
        }}
        fieldRef={(el) => {
          fieldRefs.current.application_url = el;
        }}
      />
    </>
  );
};

export default SettingForm;
